import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Dot, Loader2, MessageSquare, Pencil, SquareCheck, Trash2 } from 'lucide-react'
import { CrmTask, priorityOptions, taskStatusOptions, taskTypeOptions } from 'types/crm/task'
import ContactEmptyState from '../ContactEmptyState'
import { ContactOwner } from 'types/crm/contacts'
import Spinner from 'components/Loaders/Spinner'
import { CrmCommentHooks } from 'types/crm/comments'
import CrmCommentsPanel from '../Comments/CrmCommentsPanel'
import ActionsDropdown from 'components/common/ActionsDropdown'
import InlinePillSelect, { InlinePillColor } from './InlinePillSelect'
import { formatDayMonthYear, formatTimeIfToday } from 'utils/date'


const priorityColorMap: Record<string, InlinePillColor> = {
  HIGH: { text: 'text-red-600' },
  MEDIUM: { text: 'text-amber-600' },
  LOW: { text: 'text-green-600' },
}

const taskTypeColorMap: Record<string, InlinePillColor> = {
  TODO: { text: 'text-red-600' },
  CALL: { text: 'text-amber-600' },
  EMAIL: { text: 'text-green-600' },
}

const taskStatusColorMap: Record<string, InlinePillColor> = {
  NOT_STARTED: { text: 'text-blue-600' },
  IN_PROGRESS: { text: 'text-purple-600' },
  WAITING: { text: 'text-teal-600' },
  COMPLETED: { text: 'text-green-600' },
}


type TaskUpdatePayload = Partial<Pick<CrmTask, 'taskType' | 'priority' | 'taskStatus'>>

interface TaskListProps {
  tasks: CrmTask[]
  isLoading?: boolean
  emptyMessage?: string
  hasMore?: boolean
  isFetchingMore?: boolean
  onLoadMore?: () => void
  onToggleComplete?: (taskUuid: string, isCompleted: boolean) => void
  onAddTask?: () => void
  onEditTask?: (task: CrmTask) => void
  onDeleteTask?: (taskUuid: string) => void
  /** Inline update for quick-edit fields (task type, priority). */
  onUpdateTask?: (taskUuid: string, updates: TaskUpdatePayload) => void | Promise<void>
  isFormOpen?: boolean
  owners: ContactOwner[]
  parentUuid: string
  commentHooks: CrmCommentHooks
  onCommentAdded?: () => void
  highlightTaskUuid?: string | null
  onTaskHighlighted?: () => void
}
const HIGHLIGHT_DURATION_MS = 2500

interface TaskItemProps {
  task: CrmTask
  onToggleComplete?: (taskUuid: string, isCompleted: boolean) => void
  onEditTask?: (task: CrmTask) => void
  onDeleteTask?: (taskUuid: string) => void
  onUpdateTask?: (taskUuid: string, updates: TaskUpdatePayload) => void | Promise<void>
  owners: ContactOwner[]
  parentUuid: string
  commentHooks: CrmCommentHooks
  onCommentAdded?: () => void
  isHighlighted?: boolean
  onHighlighted?: () => void
}

/** Small inline separator that collapses on narrow screens where the meta row wraps. */
const MetaDot = () => <Dot className="hidden sm:block w-4 h-4 shrink-0 text-neutral-faded" />

const TaskItem = ({
  task,
  owners,
  onToggleComplete,
  onEditTask,
  onDeleteTask,
  onUpdateTask,
  parentUuid,
  commentHooks,
  onCommentAdded,
  isHighlighted = false,
  onHighlighted,
}: TaskItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showHighlight, setShowHighlight] = useState(isHighlighted)
  const [isDescExpanded, setIsDescExpanded] = useState(false)
  const [isDescOverflowing, setIsDescOverflowing] = useState(false)
  const itemRef = useRef<HTMLDivElement | null>(null)
  const descTextRef = useRef<HTMLParagraphElement>(null)
  // const isCompleted = task.taskStatus === 'COMPLETED'

  useEffect(() => {
    if (!isHighlighted) return
    setShowHighlight(true)
    itemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const timer = setTimeout(() => {
      setShowHighlight(false)
      onHighlighted?.()
    }, HIGHLIGHT_DURATION_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHighlighted])

  useEffect(() => {
    if (!isExpanded) return
    const el = descTextRef.current
    if (!el) return

    const measure = () => setIsDescOverflowing(el.scrollHeight > el.clientHeight)
    measure()

    // Re-measure on resize so the Expand/Collapse toggle stays accurate.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [isExpanded, isDescExpanded, task.description])

  return (
    <div
      ref={itemRef}
      className={`rounded-lg bg-primary-light p-3 sm:p-4 space-y-3 overflow-hidden transition-shadow duration-500 ${showHighlight ? 'ring-2 ring-color-accent' : 'ring-2 ring-transparent'
        }`}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        <span className="p-1.5 rounded-lg bg-primary-variant shrink-0">
          <SquareCheck className="w-4 h-4 text-color-accent" />
        </span>

        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse task' : 'Expand task'}
          className="flex items-center shrink-0 gap-1 text-xs text-accent hover:opacity-80 transition-opacity mt-1"
        >
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-medium whitespace-pre-wrap break-words min-w-0 text-neutral capitalize">
              {task.title}
            </p>

            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <span className=" xs:inline text-xs whitespace-nowrap text-neutral-faded">
                {formatTimeIfToday(task.createdDate)}
              </span>

              {(onEditTask || onDeleteTask) && (
                <ActionsDropdown
                  size="sm"
                  actions={[
                    ...(onEditTask
                      ? [{ label: 'Edit Task', icon: <Pencil className="w-4 h-4" />, onClick: () => onEditTask(task) }]
                      : []),
                    ...(onDeleteTask
                      ? [
                        {
                          label: 'Delete Task',
                          icon: <Trash2 className="w-4 h-4" />,
                          variant: 'danger' as const,
                          onClick: () => onDeleteTask(task.uuid),
                        },
                      ]
                      : []),
                  ]}
                />
              )}
            </div>
          </div>

          {/* Created date moves under the title on the narrowest screens. */}
          {/* <span className="xs:hidden block text-xs text-neutral-faded">
            {formatTimeIfToday(task.createdDate)}
          </span> */}

          {isExpanded && task.description && (
            <div className="mt-1">
              <p
                ref={descTextRef}
                className={`text-sm text-neutral-faded whitespace-pre-wrap break-words ${isDescExpanded ? '' : 'line-clamp-4'
                  }`}
              >
                {task.description}
              </p>
              {isDescOverflowing && (
                <button
                  type="button"
                  onClick={() => setIsDescExpanded((prev) => !prev)}
                  className="flex items-center gap-1 text-xs text-color-accent hover:opacity-80 transition-opacity mt-1"
                >
                  {isDescExpanded ? 'Collapse' : 'Expand'}
                  <ChevronDown className={`w-3 h-3 transition-transform ${isDescExpanded ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>
          )}

          {/* Metadata: wraps on narrow widths, single line from sm up. */}
          <div className="mt-2 space-y-1.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-0">
              <span className="text-xs whitespace-nowrap text-neutral-faded">
                Due {formatDayMonthYear(task.dueAt)}
              </span>

              {task.taskOwner && (
                <>
                  <MetaDot />
                  <span className="text-xs text-neutral-faded min-w-0 max-w-full truncate">
                    Assigned to <span className="text-neutral-faded">{task.taskOwner.name}</span>
                  </span>
                </>
              )}

              <MetaDot />
              <InlinePillSelect
                value={task.taskType}
                options={taskTypeOptions}
                colorMap={taskTypeColorMap}
                disabled={!onUpdateTask}
                onChange={(value) => onUpdateTask?.(task.uuid, { taskType: value as CrmTask['taskType'] })}
              />

              <MetaDot />
              <InlinePillSelect
                value={task.priority}
                options={priorityOptions}
                colorMap={priorityColorMap}
                disabled={!onUpdateTask}
                onChange={(value) => onUpdateTask?.(task.uuid, { priority: value as CrmTask['priority'] })}
              />

              <MetaDot />
              <InlinePillSelect
                value={task.taskStatus}
                options={taskStatusOptions}
                colorMap={taskStatusColorMap}
                disabled={!onUpdateTask}
                onChange={(value) => onUpdateTask?.(task.uuid, { taskStatus: value as CrmTask['taskStatus'] })}
              />
            </div>

            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              className="flex w-fit items-center gap-1 text-xs text-color-accent hover:opacity-80 transition-opacity"
            >
              <MessageSquare className="w-3 h-3 shrink-0 text-color-accent" />
              {task.commentCount > 0 ? `${task.commentCount} comment${task.commentCount > 1 ? 's' : ''}` : 'Add Comment'}
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="pt-2 mb-2 sm:mb-4 border-t border-primary-subtle">
          <CrmCommentsPanel
            parentUuid={parentUuid}
            entityUuid={task.uuid}
            commentHooks={commentHooks}
            mentionUsers={owners}
            onCommentAdded={onCommentAdded}
          />
        </div>
      )}
    </div>
  )
}

const TaskList = ({
  tasks,
  isLoading,
  emptyMessage = 'No tasks yet',
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
  onToggleComplete,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onUpdateTask,
  isFormOpen = false,
  owners,
  parentUuid,
  commentHooks,
  onCommentAdded,
  highlightTaskUuid,
  onTaskHighlighted,
}: TaskListProps) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!hasMore || !onLoadMore) return
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore()
      },
      { threshold: 0.1 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, onLoadMore])

  useEffect(() => {
    if (!highlightTaskUuid || !hasMore || !onLoadMore) return
    const found = tasks?.some((t) => t.uuid === highlightTaskUuid)
    if (!found) onLoadMore()
  }, [highlightTaskUuid, tasks, hasMore, onLoadMore])

  if (isLoading) return <Spinner />

  if (!tasks || tasks.length === 0) {
    if (isFormOpen) return null
    return (
      <ContactEmptyState
        icon={SquareCheck}
        title="No tasks yet"
        description="Create a task to keep track of the next step with your team."
        action={onAddTask ? { label: 'Add task', onClick: onAddTask } : undefined}
      />
    )
  }

  return (
    <div className="w-full min-w-0 space-y-2.5 sm:space-y-3">
      {tasks.map((task) => (
        <TaskItem
          key={task.uuid}
          task={task}
          onToggleComplete={onToggleComplete}
          onEditTask={onEditTask}
          onDeleteTask={onDeleteTask}
          onUpdateTask={onUpdateTask}
          owners={owners}
          parentUuid={parentUuid}
          commentHooks={commentHooks}
          onCommentAdded={onCommentAdded}
          isHighlighted={!!highlightTaskUuid && task.uuid === highlightTaskUuid}
          onHighlighted={onTaskHighlighted}
        />
      ))}

      <div className="flex flex-col items-center gap-2 py-2">
        {isFetchingMore && <Loader2 className="w-5 h-5 animate-spin text-neutral-medium" />}
        {hasMore && <div ref={sentinelRef} className="h-1 w-full" />}
      </div>
    </div>
  )
}

export default TaskList